import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";

const WHATSAPP_URL = "https://wa.me/33769310806";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      navigate(data.role === "admin" ? "/admin" : "/espace");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-cream px-6 py-12 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.55, ease: "easeOut" }}
        className="w-full max-w-[420px]"
      >
        {/* Logo */}
        <div className="flex justify-center">
          <span data-testid="elevate-logo" className="logo-elevate text-3xl sm:text-4xl">
            elevate
          </span>
        </div>

        <motion.img
          data-testid="login-portrait"
          src="/img/fanny-login.webp"
          alt="Fanny, ta coach"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.15, ease: "easeOut" }}
          className="mx-auto mt-6 w-56 sm:w-64"
          style={{ WebkitMaskImage: "linear-gradient(to bottom, #000 70%, transparent 100%)", maskImage: "linear-gradient(to bottom, #000 70%, transparent 100%)" }}
        />

        {/* Titre + sous-titre */}
        <div className="mt-4 text-center">
          <h1 className="font-sans text-4xl font-medium text-bordeaux-dark sm:text-5xl">Bienvenue</h1>
          <p className="mt-3 font-sans text-sm font-light text-bordeaux sm:text-base">
            Connecte-toi pour accéder à ton espace membre.
          </p>
        </div>

        {/* Formulaire */}
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="font-sans text-sm font-medium text-bordeaux-dark">Email</label>
            <input
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-2 w-full rounded-xl border border-line bg-white px-5 py-4 font-sans text-ink outline-none transition placeholder:text-ink-muted focus:border-bordeaux focus:ring-2 focus:ring-bordeaux/10"
              placeholder="ton@email.com"
            />
          </div>
          <div>
            <label className="font-sans text-sm font-medium text-bordeaux-dark">Mot de passe</label>
            <div className="relative">
              <input
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 w-full rounded-xl border border-line bg-white px-5 py-4 pr-12 font-sans text-ink outline-none transition placeholder:text-ink-muted focus:border-bordeaux focus:ring-2 focus:ring-bordeaux/10"
                placeholder="••••••••"
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                className="absolute right-4 top-1/2 mt-1 -translate-y-1/2 text-ink-muted transition hover:text-bordeaux"
              >
                {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
              </button>
            </div>
          </div>

          {error && (
            <p data-testid="login-error" className="rounded-lg bg-bordeaux-tint px-4 py-3 font-sans text-sm font-light text-bordeaux">
              {error}
            </p>
          )}

          <button
            data-testid="login-submit-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-bordeaux py-4 font-sans text-base font-medium text-white transition hover:bg-bordeaux-dark disabled:opacity-60"
          >
            {loading ? "Connexion…" : "Accéder à mon espace"}
          </button>

          <p className="text-center">
            <button
              data-testid="forgot-password-link"
              type="button"
              onClick={() => navigate("/mot-de-passe-oublie")}
              className="font-sans text-sm font-light text-bordeaux underline decoration-bordeaux/30 underline-offset-4 transition hover:text-bordeaux-dark hover:decoration-bordeaux"
            >
              Mot de passe oublié ?
            </button>
          </p>
        </form>

        {/* Aide WhatsApp */}
        <p className="mt-10 text-center font-sans text-sm font-light text-ink-muted">
          Un souci de connexion ?{" "}
          <a
            data-testid="whatsapp-link"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="font-normal text-bordeaux underline decoration-bordeaux/30 underline-offset-4 transition hover:text-bordeaux-dark hover:decoration-bordeaux"
          >
            Envoie-moi un message sur WhatsApp.
          </a>
        </p>
      </motion.div>
    </div>
  );
}
